import { Alert, Button, Card, Col, Form, message, Radio, Row, Select, Typography } from "antd";
import { useQuery } from "@tanstack/react-query";
import { DEFAULT_PROVINCE, fetchProvinces } from "../../services/province.service";
import { useForm } from "antd/es/form/Form";
import { useParams } from "react-router";
import { useEffect } from "react";
import { userService } from "../../services/user.service";
import CardFooterAction from "../../components/CardFooterAction";
import { directorateOptions, positionOptions } from "../../data/dummyUser";
import { useGetDetailApi, useStoreUpdateApi } from "../../hooks/useApi";
import CustomTextInput from "../../components/CustomTextInput";
import { useMe } from "../../hooks/useMe";

export default function UserForm() {
  const { id } = useParams();
  const [form] = useForm();
  const [messageApi, messageHolder] = message.useMessage();

  const {
    data: provinces = [],
    isPending: provincesLoading,
    isFetching: provincesFetching,
    isError: provincesError,
    refetch: reloadProvinces,
  } = useQuery({
    queryKey: ["user-provinces"],
    queryFn: fetchProvinces,
    staleTime: 24 * 60 * 60 * 1000,
    retry: 1,
  });
  const provinceOptions = provinces.map((province) => ({
    label: province.nama,
    value: province.nama,
  }));

  const { data } = useGetDetailApi({
    id,
    serviceFn: userService.findById,
    queryKey: ["user"],
  });

  const { storeMutation, updateMutation } = useStoreUpdateApi({
    service: userService,
    form: form,
    messageApi: messageApi,
    queryKey: "user",
    route: "/user",
  });

  const { data: userDataProfile } = useMe();
  const userPosition = userDataProfile?.data?.position?.toLowerCase();
  const isSuperadminOrAdmin =
    userPosition === "superadmin" ||
    userPosition === "admin" ||
    userPosition === undefined;

  const isDirektur = userPosition === "direktur";

  let canSubmit = false;
  if (isSuperadminOrAdmin) {
    canSubmit = true;
  } else if (isDirektur) {
    canSubmit = true;
  }

  const filteredPositionOptions = positionOptions.filter((opt) => {
    if (isSuperadminOrAdmin) {
      return true;
    }
    if (isDirektur) {
      return opt.value?.toLowerCase() === "verifikator" || opt.label?.toLowerCase() === "verifikator";
    }
    return false;
  });

  useEffect(() => {
    if (id && data) {
      form.setFieldsValue({
        ...data,
        status: data.status ? "active" : "inactive",
        province: data.province || DEFAULT_PROVINCE,
      });
    }
  }, [data, id, form]);

  const isPending = storeMutation.isPending || updateMutation.isPending;

  const handleSubmit = (values) => {
    if (!id) {
      storeMutation.mutate(values);
    } else {
      updateMutation.mutate({ id, data: values });
    }
  };

  return (
    <>
      {messageHolder}
      <Card
        title={(id ? "Edit" : "Buat") + " User"}
        actions={[
          <CardFooterAction
            key={"1"}
            route={"/user"}
            onClick={() => form.submit()}
            loading={isPending}
            hideSubmit={!canSubmit}
          />,
        ]}
      >
        <Form
          initialValues={{
            status: "active",
            province: DEFAULT_PROVINCE,
          }}
          layout="vertical"
          form={form}
          onFinish={handleSubmit}
          disabled={isPending || !canSubmit}
        >
          <CustomTextInput
            label="Nama Lengkap"
            name="fullName"
            rules={[{ required: true, message: "Nama Lengkap harus diisi" }]}
            disabled={isPending}
          />

          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                name={"position"}
                label={
                  <Typography.Text>
                    Jabatan
                    <Typography.Text style={{ color: "#940808" }}>
                      *
                    </Typography.Text>
                  </Typography.Text>
                }
                required={false}
                rules={[
                  {
                    required: true,
                    message: "Jabatan harus diisi",
                  },
                ]}
              >
                <Select
                  size="large"
                  style={{
                    fontSize: 14,
                  }}
                  options={filteredPositionOptions}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name={"directorate"}
                label={
                  <Typography.Text>
                    Direktorat
                    <Typography.Text style={{ color: "#940808" }}>
                      *
                    </Typography.Text>
                  </Typography.Text>
                }
                required={false}
                rules={[
                  {
                    required: true,
                    message: "Direktorat harus diisi",
                  },
                ]}
              >
                <Select size="large" options={directorateOptions} />
              </Form.Item>
            </Col>
          </Row>

          {provincesError && (
            <Alert
              type="error"
              showIcon
              message="Gagal memuat data provinsi. Silakan coba lagi."
              style={{ marginBottom: 16 }}
              action={
                <Button size="small" onClick={() => reloadProvinces()} loading={provincesFetching}>
                  Coba Lagi
                </Button>
              }
            />
          )}
          <Form.Item
            name="province"
            label={
              <Typography.Text>
                Provinsi<Typography.Text style={{ color: "#940808" }}>*</Typography.Text>
              </Typography.Text>
            }
            required={false}
            rules={[
              { required: true, message: "Provinsi harus dipilih" },
              {
                validator: (_, value) => {
                  if (!value) return Promise.resolve();
                  if (!provinces.length) {
                    return Promise.reject(new Error("Data provinsi belum tersedia. Silakan coba lagi."));
                  }
                  return provinces.some((province) => province.nama === value)
                    ? Promise.resolve()
                    : Promise.reject(new Error("Pilih provinsi yang tersedia"));
                },
              },
            ]}
            extra="Data lembaga vokasi yang ditampilkan mengikuti provinsi user."
          >
            <Select
              size="large"
              showSearch
              optionFilterProp="label"
              placeholder="Pilih Provinsi"
              options={provinceOptions}
              loading={provincesFetching}
              disabled={isPending || !canSubmit || provincesLoading || !provinces.length}
              notFoundContent="Provinsi tidak ditemukan"
            />
          </Form.Item>

          <CustomTextInput
            label="Nomor Telepon"
            name="phone"
            starLabel={false}
            disabled={isPending}
            phone
          />

          <CustomTextInput
            label="Email"
            name="email"
            rules={[
              {
                required: true,
                message: "Email harus diisi",
              },
              {
                type: "email",
                message: "Format email tidak valid",
              },
            ]}
            disabled={isPending}
          />

          {!id && (
            <>
              <Row gutter={16}>
                <Col xs={24} md={12}>
                  <CustomTextInput
                    label="Password"
                    name="password"
                    password
                    rules={[
                      {
                        required: true,
                        message: "Password harus diisi",
                      },
                      {
                        min: 8,
                        message: "Password minimal 8 karakter",
                      },
                      {
                        pattern: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[\W_]).{8,}$/,
                        message: "Password harus mengandung huruf besar, huruf kecil, angka, dan simbol",
                      },
                    ]}
                    disabled={isPending}
                  />
                </Col>

                <Col xs={24} md={12}>
                  <CustomTextInput
                    label="Konfirmasi Password"
                    name="confirmPassword"
                    password
                    dependencies={["password"]}
                    rules={[
                      {
                        required: true,
                        message: "Mohon konfirmasi password",
                      },
                      ({ getFieldValue }) => ({
                        validator(_, value) {
                          if (!value || getFieldValue("password") === value) {
                            return Promise.resolve();
                          }
                          return Promise.reject(
                            new Error("Password tidak sesuai!")
                          );
                        },
                      }),
                    ]}
                    disabled={isPending}
                  />
                </Col>
              </Row>
            </>
          )}

          <Form.Item
            name={"status"}
            label={
              <Typography.Text>
                Status
                <Typography.Text style={{ color: "#940808" }}>
                  *
                </Typography.Text>
              </Typography.Text>
            }
            required={false}
            rules={[
              {
                required: true,
                message: "Status harus diisi",
              },
            ]}
          >
            <Radio.Group>
              <Radio style={{ marginRight: 80 }} value={"active"}>
                Active
              </Radio>
              <Radio value={"inactive"}>Inactive</Radio>
            </Radio.Group>
          </Form.Item>
        </Form>
      </Card>
    </>
  );
}
